<template>
  <div class="sync-indicator" :data-show="+shown" @mouseleave="listShown = false">
    <div
      v-if="listShown && uploads.length"
      class="absolute top-0 right-full mr-2 flex w-72 flex-col gap-3 rounded-lg border border-line bg-popover-solid p-3 text-text shadow-lg"
    >
      <div v-for="upload in uploads" :key="upload.id" class="flex min-w-0 flex-col gap-1">
        <div class="flex justify-between gap-2 text-label">
          <span class="truncate">{{ upload.name }}</span>
          <span class="shrink-0 text-text-muted tabular-nums">{{ fileSize(upload.total) }}</span>
        </div>
        <div class="h-1 overflow-hidden rounded-full bg-line">
          <i class="block h-full" :class="barColor(upload)" :style="{ width: `${percent(upload)}%` }"></i>
        </div>
        <div class="flex flex-wrap items-center gap-2 text-unit" :class="upload.state === 'failed' ? 'text-red' : 'text-text-muted'">
          {{ statusText(upload) }}
          <template v-if="upload.state === 'failed'">
            <SimpleButton size="xs" :text="$t('uploads.retry')" @click="retryUpload(upload)"/>
            <SimpleButton size="xs" variant="ghost" :text="$t('uploads.remove')" @click="dropUpload(upload)"/>
          </template>
        </div>
      </div>
    </div>
    <button
      class="pill"
      :class="failedCount && 'failed'"
      :title="uploads.length ? `${uploadsProgress}%` : $t('system.syncing')"
      @mouseenter="listShown = true"
      @click="listShown = !listShown"
    >
      <span v-if="failedCount" class="text-unit font-semibold whitespace-nowrap">
        {{ $t('uploads.not_uploaded', { count: failedCount }) }}
      </span>
      <template v-else-if="uploads.length">
        <span class="bar"><i :style="{ width: `${uploadsProgress}%` }"></i></span>
        <span class="text-unit font-semibold tabular-nums">{{ uploadsProgress }}%</span>
      </template>
      <Loader v-else/>
    </button>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import humanize from 'humanize';
import {
  uploads, uploadsProgress, retryUpload, dropUpload, MAX_UPLOAD_RETRIES,
} from '@/utils/uploadQueue';

const store = useStore();
const { t } = useI18n();

const listShown = ref(false);
const syncing = computed(() => store.state.system.syncing);
const shown = computed(() => syncing.value || uploads.length > 0);
const failedCount = computed(() => uploads.filter(u => u.state === 'failed').length);

const fileSize = bytes => humanize.filesize(bytes, 1024, 1);
const percent = upload => Math.floor((upload.loaded / upload.total) * 100);
const barColor = upload => ({
  failed: 'bg-red',
  done: 'bg-green',
  retry: 'bg-amber',
  offline: 'bg-amber',
}[upload.state] || 'bg-accent');

function statusText(upload) {
  switch (upload.state) {
    case 'uploading': return `${fileSize(upload.loaded)} / ${fileSize(upload.total)}`;
    case 'retry': return t('uploads.retry_attempt', { attempt: upload.retries, max: MAX_UPLOAD_RETRIES });
    case 'announcing': return t('uploads.queued');
    case 'uploaded': return t('uploads.saving');
    case 'failed': return t(`uploads.errors.${upload.error.reason}`, { limit: upload.error.limit && fileSize(upload.error.limit) });
    default: return t(`uploads.${upload.state}`);
  }
}
</script>

<style lang="scss" scoped>
// same spot and look as the sync loader it replaces: a tab on the right edge, half transparent until hovered
.sync-indicator {
  position: fixed;
  right: 0;
  top: 50vh;
  margin-top: -9.375rem;
  z-index: var(--z-system);
  display: flex;
  align-items: flex-start;
  transition: transform 0.3s;
  &[data-show='0'] {
    transform: translateX(100%);
  }
}

.pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem;
  border: 0;
  border-top-left-radius: 2.5rem;
  border-bottom-left-radius: 2.5rem;
  background: var(--accent);
  color: var(--on-accent);
  opacity: 0.5;
  transition: opacity 0.3s;
  &:hover {
    opacity: 1;
  }
  &.failed {
    background: var(--red);
    color: var(--on-red);
    opacity: 1;
  }

  :deep(.loader) {
    margin: 0;
    width: 0.625rem;
    height: 0.625rem;
    border-color: transparent;
    border-left-color: var(--on-accent);
  }
}

.bar {
  display: block;
  width: 4rem;
  height: 0.25rem;
  overflow: hidden;
  border-radius: 1rem;
  background: color-mix(in srgb, var(--on-accent) 20%, transparent);
  i {
    display: block;
    height: 100%;
    background: var(--on-accent);
    transition: width 0.2s linear;
  }
}
</style>
